import { Timestamp } from '@bufbuild/protobuf';
import {
  Alert,
  Breadcrumb,
  Button,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Form,
  notification,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { generatePath, Link, useNavigate, useParams } from 'react-router';

import { ServiceType } from 'protocol/api/billing_new/dto_services_new_pb';

import { PATHS } from '@/shared/config';
import {
  GroupNamesDefaultMessage,
  lifeTimeMonthDefaultMessages,
  ServiceNamesDefaultMessage,
} from '@/shared/config/i18n';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { shouldAskAdditionalInfo } from '@/shared/lib/shouldAskAdditionalData';
import { Layout } from '@/shared/ui';

import { authModel } from '@/entities/auth';
import {
  billingModel,
  EffectToAddType,
  getDefaultScheduledStartAt,
  getMinimumScheduledStartAt,
  i18n as billingI18n,
  SUBSCRIPTION_ACTIVATION_LIMIT_TRIGGER_OPTIONS,
  SubscriptionActivationLimitTriggerOption,
} from '@/entities/billing';
import { organizationModel } from '@/entities/organization';
import { organizationsModel } from '@/entities/organizations';

import { AdditionalDataForm } from '@/features/additionalDataForm';
import {
  BillingProductAccessGuard,
  useBillingProductAccessGuard,
} from '@/features/billingProductAccessGuard';
import {
  CUSTOM_PRICE_FIELD_NAME,
  CustomPriceField,
  getInvoicePrice,
  normalizeCustomPrice,
} from '@/features/customPrice';

import styles from './AddSubscription.module.scss';
import { i18n, SubscriptionActivationMode } from './config';

const { Title } = Typography;
const { Panel } = Collapse;

const getDatePickerDisabledTime = (
  currentDate: Dayjs | null,
  minimumStartAt: Dayjs,
) => {
  if (!currentDate || !currentDate.isSame(minimumStartAt, 'day')) {
    return {};
  }

  return {
    disabledHours: () =>
      Array.from({ length: minimumStartAt.hour() }, (_, hour) => hour),
    disabledMinutes: (selectedHour: number) =>
      selectedHour !== minimumStartAt.hour()
        ? []
        : Array.from(
            { length: minimumStartAt.minute() },
            (_, minute) => minute,
          ),
  };
};

type AddSubscriptionFormType = {
  autoRenewal: boolean;
  activationMode: SubscriptionActivationMode;
  subscriptionID?: string;
  lifeTimeMonths?: number;
  startAt?: Dayjs;
  activateOnLimitTrigger?: SubscriptionActivationLimitTriggerOption;
  customPrice?: number | null;
};

const selectNewOrderLoadingState =
  billingModel.selectors.getLoadingStateSelector('newOrder');

export const AddSubscription: FC = () => {
  const [isAdditionalFormOpen, setIsAdditionalFormOpen] =
    useState<boolean>(false);
  const [isProductsLoading, setIsProductsLoading] = useState(false);

  const { organizationID } = useParams<{ organizationID: string }>();

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const organization = useAppSelector(
    organizationModel.selectors.selectOrganization,
  );
  const listOrganization = useAppSelector(
    organizationsModel.selectors.selectById(organizationID),
  );
  const canEditInventoryProducts = useAppSelector(
    authModel.selectors.selectCanEditInventoryProducts,
  );
  const allSubscriptions = useAppSelector(
    billingModel.selectors.selectAvailableSubscriptions,
  );
  const subscriptions = canEditInventoryProducts
    ? allSubscriptions
    : allSubscriptions.filter((product) => !product.AvailableForAdminsOnly);

  const newOrderLoadingState = useAppSelector(selectNewOrderLoadingState);

  const {
    ID: accountID,
    BillingZones,
    BillingInformation,
  } = useAppSelector(billingModel.selectors.selectAccountInfo);
  const currentAccountSubscription = useAppSelector(
    billingModel.selectors.selectCurrentSubscription,
  );
  const scheduledSubscription = useAppSelector(
    billingModel.selectors.selectScheduledSubscription,
  );

  const BillingZone = BillingZones?.at(0);

  const { formatMessage, formatNumber } = useIntl();
  const billingProductAccessState = useBillingProductAccessGuard({
    accessDeniedDescription: formatMessage({
      id: 'newSubscriptionPage.accessDenied.message',
      defaultMessage:
        'You do not have permission to add a subscription for this organization.',
    }),
    organizationID,
  });
  // TODO [2|l] devtools throw warning that form doesnt connect to any Form component but its connected. Need to solve this issue
  const [form] = Form.useForm<AddSubscriptionFormType>();

  Form.useWatch('subscriptionID', form);

  const selectedSubscriptionID = form.getFieldValue('subscriptionID');

  const selectedSubscription = subscriptions.find(
    ({ SKU }) => SKU === selectedSubscriptionID,
  );
  const hasCurrentSubscription = Boolean(currentAccountSubscription);
  const hasScheduledSubscription = Boolean(scheduledSubscription);
  const isBlockedByAutoRenew = Boolean(currentAccountSubscription?.AutoRenewal);
  const isCreateBlocked = hasScheduledSubscription || isBlockedByAutoRenew;

  Form.useWatch('activationMode', form);

  const isAutoRenewalChecked = Form.useWatch('autoRenewal', form);
  const customPrice = normalizeCustomPrice(
    Form.useWatch(CUSTOM_PRICE_FIELD_NAME, form),
  );

  const activationMode: SubscriptionActivationMode =
    form.getFieldValue('activationMode') ?? 'immediately';
  // Billing refuses a second subscription for the same billing zone, and a
  // limit trigger needs a subscription whose limits can actually run out.
  const isImmediateModeDisabled = hasCurrentSubscription;
  const isLimitModeDisabled = !hasCurrentSubscription;
  const defaultScheduledStartAt = getDefaultScheduledStartAt(
    currentAccountSubscription,
  );

  const formatDateTime = (value?: { seconds?: bigint | number }) => {
    if (!value?.seconds) {
      return formatMessage({
        id: 'global.notAvailable',
        defaultMessage: 'Not available',
      });
    }

    const date = new Date(Number(value.seconds) * 1000);

    return dayjs(date).format('YYYY-MM-DD HH:mm');
  };

  const onFinish = async () => {
    if (!selectedSubscription || isCreateBlocked) {
      return;
    }

    // Belt and braces on top of the disabled radio: billing rejects this and the
    // admin would only see a raw error.
    if (activationMode === 'immediately' && hasCurrentSubscription) {
      return;
    }

    const isAutoRenewal = form.getFieldValue('autoRenewal');
    const customPriceToBill = normalizeCustomPrice(
      form.getFieldValue(CUSTOM_PRICE_FIELD_NAME),
    );
    const startAt =
      activationMode === 'atDate' ? form.getFieldValue('startAt') : undefined;
    const activateOnLimitTrigger =
      activationMode === 'onLimitTrigger'
        ? form.getFieldValue('activateOnLimitTrigger')
        : undefined;

    const effectToAdd: EffectToAddType = {
      case: 'SubscriptionAdd',
      value: {
        Subscription: selectedSubscription,
        IsAutoRenewal: selectedSubscription.Name.includes('KOL')
          ? false
          : isAutoRenewal,
        StartAt: startAt ? Timestamp.fromDate(startAt.toDate()) : undefined,
        ActivateOnLimitTrigger: activateOnLimitTrigger,
      },
    };

    if (
      shouldAskAdditionalInfo(BillingZone, BillingInformation) &&
      getInvoicePrice(selectedSubscription.Price, customPriceToBill) > 0
    ) {
      dispatch(
        billingModel.actions.setOrderToAdd({
          Effect: effectToAdd,
          CustomPrice: customPriceToBill,
        }),
      );

      setIsAdditionalFormOpen(true);
    } else {
      try {
        dispatch(
          billingModel.actions.setLoading({
            loaderKey: 'newOrder',
            loaderState: 'pending',
          }),
        );

        await dispatch(
          billingModel.thunks.createOrder({
            AccountID: accountID,
            Effect: effectToAdd,
            CustomPrice: customPriceToBill,
          }),
        ).unwrap();

        navigate(generatePath(PATHS.organization, { organizationID }));
      } catch (error) {
        dispatch(
          billingModel.actions.setLoading({
            loaderKey: 'newOrder',
            loaderState: 'failed',
          }),
        );

        notification.error({
          message:
            error instanceof Error
              ? error.message
              : formatMessage({
                  id: 'global.network.error',
                  defaultMessage: 'Network error',
                }),
        });
      } finally {
        dispatch(
          billingModel.actions.setLoading({
            loaderKey: 'newOrder',
            loaderState: 'idle',
          }),
        );
      }
    }
  };

  useEffect(() => {
    const getProducts = async () => {
      setIsProductsLoading(true);

      try {
        const { AvailableProducts } = await dispatch(
          billingModel.thunks.getAvailableProducts({
            Condition: {
              case: 'ByAccountID',
              value: accountID,
            },
          }),
        ).unwrap();

        dispatch(billingModel.actions.setAvailableProducts(AvailableProducts));
      } finally {
        setIsProductsLoading(false);
      }
    };

    if (accountID) {
      getProducts();
    }
  }, [accountID]);

  useEffect(() => {
    if (
      hasCurrentSubscription &&
      defaultScheduledStartAt &&
      !form.isFieldTouched('startAt') &&
      !form.getFieldValue('startAt')
    ) {
      form.setFieldsValue({
        startAt: defaultScheduledStartAt,
      });
    }
  }, [defaultScheduledStartAt, form, hasCurrentSubscription]);

  // initialValues is evaluated on first render, before accountInfo has streamed
  // in, so the default mode has to be seeded once we know whether a current
  // subscription exists. Guarded on isFieldTouched so a late stream update never
  // overwrites the admin's own choice.
  useEffect(() => {
    if (form.isFieldTouched('activationMode')) {
      return;
    }

    form.setFieldsValue({
      activationMode: hasCurrentSubscription ? 'atDate' : 'immediately',
    });
  }, [form, hasCurrentSubscription]);

  if (billingProductAccessState !== 'allowed') {
    return <BillingProductAccessGuard state={billingProductAccessState} />;
  }

  return (
    <Layout.Content>
      <Breadcrumb
        className="p2"
        style={{ padding: '24px 0' }}
        items={[
          {
            title: (
              <Link className={styles.breadcrumb} to={generatePath(PATHS.main)}>
                <FormattedMessage id="breadcrumb.home" defaultMessage="Home" />
              </Link>
            ),
          },
          {
            title: (
              <Link
                className={styles.breadcrumb}
                to={generatePath(PATHS.organization, { organizationID })}
              >
                {organization?.Name ?? listOrganization?.Name ?? ''}
              </Link>
            ),
          },
          {
            title: (
              <FormattedMessage
                id="newSubscriptionPage.title"
                defaultMessage="New subscription"
              />
            ),
          },
        ]}
      />

      <Form
        form={form}
        initialValues={{
          autoRenewal: true,
          activationMode: 'immediately',
        }}
        name="add-subscription"
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        onValuesChange={(values) => {
          if (values?.subscriptionID) {
            form.setFieldsValue({ lifeTimeMonths: undefined });
          }
        }}
      >
        {isCreateBlocked && (
          <Alert
            style={{ marginBottom: 24 }}
            type="warning"
            showIcon
            message={
              hasScheduledSubscription
                ? formatMessage({
                    id: 'addSubscription.blockedByScheduled',
                    defaultMessage:
                      'Remove the scheduled subscription before adding another one.',
                  })
                : formatMessage({
                    id: 'addSubscription.blockedByAutoRenew',
                    defaultMessage:
                      'Turn off auto-renew for the current subscription before scheduling the next one.',
                  })
            }
          />
        )}

        <Row gutter={32}>
          <Col span={12}>
            {/* Step 1 */}
            <Title level={4}>
              <FormattedMessage
                id="subscriptionForm.stepOne"
                defaultMessage="Select subscription"
              />
            </Title>

            <Form.Item
              name="subscriptionID"
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'forms.required',
                    defaultMessage: 'Field is required',
                  }),
                },
              ]}
            >
              <Spin spinning={isProductsLoading}>
                <Radio.Group>
                  <Space direction="vertical">
                    {subscriptions.map(
                      ({
                        SKU,
                        Name,
                        Price,
                        Currency,
                        Kind: subscriptionKind,
                        Services,
                      }) => (
                        <Radio key={SKU} value={SKU}>
                          <Space direction="horizontal">
                            <Collapse expandIcon={() => null} size="small">
                              <Panel
                                key={SKU}
                                header={
                                  <Title level={5} style={{ marginBottom: 0 }}>
                                    <>
                                      {Name}
                                      {' | '}
                                      <FormattedNumber
                                        minimumFractionDigits={0}
                                        maximumFractionDigits={2}
                                        value={Number(Price ?? '0')}
                                        // eslint-disable-next-line react/style-prop-object
                                        style="currency"
                                        currencyDisplay="narrowSymbol"
                                        currency={Currency}
                                      />
                                      {' | '}
                                      {subscriptionKind.case ===
                                        'Subscription' &&
                                        (subscriptionKind.value.LifeTimeDays > 0
                                          ? formatMessage(
                                              {
                                                id: 'product.lifetime.days',
                                                defaultMessage:
                                                  '{days, plural, =0 {0 days} one {One day} other {{days} days}}',
                                              },
                                              {
                                                days: subscriptionKind.value
                                                  .LifeTimeDays,
                                              },
                                            )
                                          : formatMessage(
                                              lifeTimeMonthDefaultMessages[
                                                subscriptionKind.value
                                                  .LifeTimeMonths
                                              ],
                                            ))}
                                    </>
                                  </Title>
                                }
                              >
                                <ul>
                                  {Services?.map(({ Types, Group, Kind }) => {
                                    const amount =
                                      Kind.case === 'Countable'
                                        ? Number(Kind.value.Limit)
                                        : 0;
                                    const type =
                                      Types?.length > 0
                                        ? Types.at(0)
                                        : ServiceType.ServiceTypeInvalidValue;

                                    return (
                                      <li key={`${Types.toString()}`}>
                                        {Types.length > 1
                                          ? formatMessage(
                                              GroupNamesDefaultMessage[Group],
                                              {
                                                amount,
                                              },
                                            )
                                          : formatMessage(
                                              ServiceNamesDefaultMessage[type],
                                              {
                                                amount,
                                              },
                                            )}
                                        {Types.length > 1 && (
                                          <ul>
                                            {Types.map((serviceType) => (
                                              <li key={serviceType}>
                                                {formatMessage(
                                                  ServiceNamesDefaultMessage[
                                                    serviceType
                                                  ],
                                                  { amount: 0 },
                                                )}
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </Panel>
                            </Collapse>
                          </Space>
                        </Radio>
                      ),
                    )}
                  </Space>
                </Radio.Group>
              </Spin>
            </Form.Item>

            <CustomPriceField
              listPrice={selectedSubscription?.Price}
              currency={selectedSubscription?.Currency}
              disabled={isCreateBlocked}
            />

            <Form.Item
              name="activationMode"
              label={formatMessage(
                i18n.addSubscriptionMessages.activationModeLabel,
              )}
              rules={[{ required: true }]}
            >
              <Radio.Group disabled={isCreateBlocked}>
                <Space direction="vertical">
                  <Tooltip
                    title={
                      isImmediateModeDisabled
                        ? formatMessage(
                            i18n.addSubscriptionMessages
                              .immediatelyDisabledHint,
                          )
                        : undefined
                    }
                  >
                    <Radio
                      value="immediately"
                      disabled={isImmediateModeDisabled}
                    >
                      {formatMessage(
                        i18n.subscriptionActivationModeMessages.immediately,
                      )}
                    </Radio>
                  </Tooltip>

                  <Radio value="atDate">
                    {formatMessage(
                      i18n.subscriptionActivationModeMessages.atDate,
                    )}
                  </Radio>

                  <Tooltip
                    title={
                      isLimitModeDisabled
                        ? formatMessage(
                            i18n.addSubscriptionMessages
                              .onLimitTriggerDisabledHint,
                          )
                        : undefined
                    }
                  >
                    <Radio
                      value="onLimitTrigger"
                      disabled={isLimitModeDisabled}
                    >
                      {formatMessage(
                        i18n.subscriptionActivationModeMessages.onLimitTrigger,
                      )}
                    </Radio>
                  </Tooltip>
                </Space>
              </Radio.Group>
            </Form.Item>

            {/*
              Only the field for the selected mode is mounted. antd runs `rules`
              for mounted items only, so unmounting is what stops a hidden
              control from failing validation, and preserve={false} drops its
              stale value so it cannot leak into the submitted effect.
            */}
            {activationMode === 'onLimitTrigger' && (
              <Form.Item
                name="activateOnLimitTrigger"
                preserve={false}
                label={formatMessage(
                  i18n.addSubscriptionMessages.limitTriggerLabel,
                )}
                extra={formatMessage(
                  i18n.addSubscriptionMessages.onLimitTriggerHint,
                )}
                rules={[
                  {
                    required: true,
                    message: formatMessage({
                      id: 'forms.required',
                      defaultMessage: 'Field is required',
                    }),
                  },
                ]}
              >
                <Select
                  disabled={isCreateBlocked}
                  placeholder={formatMessage(
                    i18n.addSubscriptionMessages.limitTriggerPlaceholder,
                  )}
                  options={SUBSCRIPTION_ACTIVATION_LIMIT_TRIGGER_OPTIONS.map(
                    (trigger) => ({
                      value: trigger,
                      label: formatMessage(
                        billingI18n.subscriptionActivationLimitTriggerMessages[
                          trigger
                        ],
                      ),
                    }),
                  )}
                />
              </Form.Item>
            )}

            {activationMode === 'atDate' && (
              <Form.Item
                name="startAt"
                preserve={false}
                label={
                  <FormattedMessage
                    id="subscriptionForm.startAt.label"
                    defaultMessage="Start date and time"
                  />
                }
                extra={
                  <Space direction="vertical" size={0}>
                    <span>
                      {hasCurrentSubscription
                        ? formatMessage(
                            {
                              id: 'subscriptionForm.startAt.requiredHint',
                              defaultMessage:
                                'Required. Choose when the next subscription should start. It must be at or after the current subscription ends on {dateTime}.',
                            },
                            {
                              dateTime: formatDateTime(
                                currentAccountSubscription?.End,
                              ),
                            },
                          )
                        : formatMessage({
                            id: 'subscriptionForm.startAt.optionalHint',
                            defaultMessage:
                              'Optional. Leave empty to start immediately. If selected, the subscription will start at the chosen date and time.',
                          })}
                    </span>
                  </Space>
                }
                rules={[
                  {
                    // The item only exists in date mode, where a date is the whole
                    // point — so it is always required here.
                    required: true,
                    message: formatMessage({
                      id: 'forms.required',
                      defaultMessage: 'Field is required',
                    }),
                  },
                  {
                    validator: async (_, value?: Dayjs) => {
                      if (!value) {
                        return;
                      }

                      // Recompute at validation time to avoid stale render-time value
                      const currentMinimum = getMinimumScheduledStartAt(
                        currentAccountSubscription,
                      );

                      if (value.valueOf() < currentMinimum.valueOf()) {
                        throw new Error(
                          hasCurrentSubscription
                            ? formatMessage(
                                {
                                  id: 'subscriptionForm.startAt.afterCurrentValidation',
                                  defaultMessage:
                                    'Start date and time must be at or after the current subscription ends on {dateTime}.',
                                },
                                {
                                  dateTime: formatDateTime(
                                    currentAccountSubscription?.End,
                                  ),
                                },
                              )
                            : formatMessage({
                                id: 'subscriptionForm.startAt.futureValidation',
                                defaultMessage:
                                  'Start date and time must be in the future.',
                              }),
                        );
                      }
                    },
                  },
                ]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  disabled={isCreateBlocked}
                  showTime={{
                    format: 'HH:mm',
                  }}
                  disabledDate={(currentDate) => {
                    const minimum = getMinimumScheduledStartAt(
                      currentAccountSubscription,
                    );
                    return currentDate
                      ? currentDate.endOf('day').valueOf() < minimum.valueOf()
                      : false;
                  }}
                  disabledTime={(currentDate) =>
                    getDatePickerDisabledTime(
                      currentDate,
                      getMinimumScheduledStartAt(currentAccountSubscription),
                    )
                  }
                  placeholder={formatMessage({
                    id: 'subscriptionForm.startAt.placeholder',
                    defaultMessage: 'Select start date and time',
                  })}
                  format="YYYY-MM-DD HH:mm"
                />
              </Form.Item>
            )}

            <Title level={4}>
              <FormattedMessage
                id="subscriptionForm.stepTwo"
                defaultMessage="Auto-renewal:"
              />

              {subscriptions.length > 0 && (
                <Form.Item
                  name="autoRenewal"
                  valuePropName="checked"
                  rules={[
                    {
                      required: false,
                    },
                  ]}
                  extra={
                    isAutoRenewalChecked &&
                    selectedSubscription &&
                    !selectedSubscription.Name.includes('KOL') &&
                    customPrice !== undefined && (
                      <span style={{ fontWeight: 400 }}>
                        {formatMessage(
                          i18n.addSubscriptionMessages.customPriceRenewalHint,
                          {
                            price: formatNumber(
                              Number(selectedSubscription.Price ?? 0),
                              {
                                style: 'currency',
                                currency: selectedSubscription.Currency,
                                currencyDisplay: 'narrowSymbol',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                              },
                            ),
                          },
                        )}
                      </span>
                    )
                  }
                >
                  <Checkbox style={{ fontWeight: 400 }}>
                    {formatMessage({
                      id: 'subscriptionForm.autoRenewal.label',
                      defaultMessage: 'Enable auto-renewal of subscription',
                    })}
                  </Checkbox>
                </Form.Item>
              )}
            </Title>
          </Col>
        </Row>

        <Button
          type="primary"
          htmlType="submit"
          disabled={isCreateBlocked}
          loading={newOrderLoadingState === 'pending'}
        >
          <span>
            <FormattedMessage
              id="subscriptionForm.addNewSubscription"
              defaultMessage="Add new subscription"
            />
          </span>
        </Button>
      </Form>

      <AdditionalDataForm
        isOpen={isAdditionalFormOpen}
        onCloseHandler={() => setIsAdditionalFormOpen(false)}
      />
    </Layout.Content>
  );
};
